import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from '../../../i18n/useTranslation';
import { Card } from '../../atoms/Card';
import { Badge } from '../../atoms/Badge';
import type { BadgeTone } from '../../atoms/Badge';
import { Tabs } from '../../atoms/Tabs';
import { Heading, Text } from '../../atoms/Typography';
import { Button } from '../../atoms/Button/Button';
import { Input } from '../../atoms/Input/Input';
import { Inline } from '../../atoms/Inline/Inline';
import { Stack } from '../../atoms/Stack/Stack';
import { Grid } from '../../atoms/Grid';
import { EmptyState } from '../../atoms/EmptyState';
import { Icon } from '../../atoms/Icon';
import { BeforeAfterSlider } from '../../atoms/BeforeAfterSlider';
import { Section } from '../../templates/Section';
import './ComparisonView.css';
import { Alert } from '../../atoms/Alert/Alert';

/** Date presets, a custom date range, or explicitly chosen photos ('photos'). */
export type ComparisonPreset = '7d' | '30d' | '90d' | 'custom' | 'photos';

export type ComparisonChangeCategory =
  | 'none'
  | 'growth'
  | 'colour_change'
  | 'algae'
  | 'livestock_change'
  | 'other';

export type ComparisonComparability = 'full' | 'limited';

export type ComparisonConfidence = 'high' | 'medium' | 'low';

export type ComparisonViewMode = 'sideBySide' | 'slider';

export type ComparisonErrorKind =
  | 'insufficientImages'
  | 'tooManyImages'
  | 'aiBudgetExceeded'
  | 'aiUnavailable'
  | 'photosTooFew'
  | 'photosTooMany'
  | 'photosInvalid'
  | 'generic';

export type ComparisonTriggeredBy = 'manual' | 'scheduled';

export interface ComparisonImage {
  src: string;
  /** Accessible alt text, e.g. "Tank photo from 3 March 2026". */
  alt: string;
  /** Pre-formatted, locale-aware capture date ("Taken on {{date}}"). */
  date: string;
  /** True when this is the closest available photo rather than one from the selected date. */
  isNearestMatch?: boolean;
}

export interface ComparisonResult {
  id: string;
  changeCategory: ComparisonChangeCategory;
  comparability: ComparisonComparability;
  confidence: ComparisonConfidence;
  /** AI-generated description — always rendered as a plain text node. */
  description: string;
  sourceImageIds: string[];
  beforeImage: ComparisonImage;
  afterImage: ComparisonImage;
  /** Photos between the earliest and the latest (explicitly chosen comparisons of 3–5 photos). */
  intermediateImages?: ComparisonImage[];
}

export interface ComparisonHistoryItem {
  id: string;
  /** Pre-formatted start date of the compared range. */
  fromDate: string;
  /** Pre-formatted end date of the compared range. */
  toDate: string;
  changeCategory: ComparisonChangeCategory;
  /** AI-generated description — always rendered as a plain text node. */
  description: string;
  comparability: ComparisonComparability;
  triggeredBy: ComparisonTriggeredBy;
}

/**
 * Screen state of the Aquarium Comparison screen (spec: docs/design/issue-1437/spec.md).
 * - `empty`: no comparison has been run yet (history is empty, no result area)
 * - `loading`: a comparison is running; selection controls are disabled
 * - `error`: the last comparison failed (`errorKind` selects the copy)
 * - `populated`: `result` is shown
 * - `partial`: `result` is shown, built from the closest available photos
 */
export type ComparisonViewState = 'loading' | 'empty' | 'error' | 'populated' | 'partial';

export interface ComparisonViewProps {
  state: ComparisonViewState;
  /** Currently selected preset; 'custom' shows the date-range inputs. */
  selectedPreset: ComparisonPreset;
  /** The photo picker shown for the 'photos' mode (see ComparisonPhotoPicker). */
  photoPicker?: ReactNode;
  /** Controlled custom range values (yyyy-mm-dd). */
  customDateFrom: string;
  customDateTo: string;
  /** Result view mode — side-by-side is the default/fallback. */
  viewMode: ComparisonViewMode;
  /** The comparison shown in the result area (new or re-opened from history). */
  result: ComparisonResult | null;
  /** Which error copy to show when `state === 'error'`. */
  errorKind: ComparisonErrorKind | null;
  /** Past comparisons, newest first. */
  history: ComparisonHistoryItem[];
  /** True when older comparisons can be loaded; shows the "Load more" button below the history. */
  hasMoreHistory?: boolean;
  /** True while the next history page is loading (the button is disabled and aria-busy). */
  isLoadingMoreHistory?: boolean;
  /** Loads the next (older) history page. */
  onLoadMoreHistory?: () => void;
  /** Id of the history item currently shown in the result area. */
  selectedHistoryId: string | null;
  /** Disables slider transitions. Defaults to the user's `prefers-reduced-motion` setting. */
  reducedMotion?: boolean;
  onSelectPreset: (preset: ComparisonPreset) => void;
  onChangeCustomDateFrom: (value: string) => void;
  onChangeCustomDateTo: (value: string) => void;
  /** Runs a comparison (empty-state action; the page header owns the primary Compare button). */
  onCompare: () => void;
  /** Re-runs the last comparison after a generic failure. */
  onRetry: () => void;
  onChangeViewMode: (mode: ComparisonViewMode) => void;
  onSelectHistoryItem: (id: string) => void;
  /** Asks to delete one past comparison (the page confirms). No delete buttons without it. */
  onDeleteHistoryItem?: (id: string) => void;
  /** Asks to delete every past comparison (the page confirms). No "Delete all" without it. */
  onDeleteAllHistory?: () => void;
  /** True while a delete is running: the delete buttons are disabled. */
  isDeletingHistory?: boolean;
  /** Shown above the history list, e.g. a delete error Alert or a live announcement. */
  historyStatus?: ReactNode;
}

/** Programmatic focus target (never in the tab order) for a newly rendered result. */
const RESULT_HEADING_PROPS = { tabIndex: -1, 'data-comparison-focus': 'result' };

const PRESETS: ComparisonPreset[] = ['7d', '30d', '90d', 'custom', 'photos'];

const CHANGE_TONES: Record<ComparisonChangeCategory, BadgeTone> = {
  none: 'neutral',
  growth: 'info',
  colour_change: 'info',
  algae: 'warning',
  livestock_change: 'info',
  other: 'neutral',
};

function comparabilityTone(comparability: ComparisonComparability): BadgeTone {
  return comparability === 'full' ? 'success' : 'warning';
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * ComparisonView — the body of the Aquarium Comparison screen: date selection,
 * the current/selected comparison result and the history of past comparisons.
 * Purely presentational: all data and handlers come in via props. The page wraps
 * it in AppLayout › PageContent(width="wide") › PageHeader (title + primary Compare).
 */
export function ComparisonView({
  state,
  selectedPreset,
  photoPicker,
  customDateFrom,
  customDateTo,
  viewMode,
  result,
  errorKind,
  history,
  hasMoreHistory = false,
  isLoadingMoreHistory = false,
  onLoadMoreHistory,
  selectedHistoryId,
  reducedMotion,
  onSelectPreset,
  onChangeCustomDateFrom,
  onChangeCustomDateTo,
  onCompare,
  onRetry,
  onChangeViewMode,
  onSelectHistoryItem,
  onDeleteHistoryItem,
  onDeleteAllHistory,
  isDeletingHistory = false,
  historyStatus,
}: ComparisonViewProps) {
  const { t } = useTranslation();
  const rootRef = useRef<HTMLDivElement>(null);
  const presetRefs = useRef<Map<ComparisonPreset, HTMLButtonElement>>(new Map());
  const previous = useRef<{ state: ComparisonViewState; resultId: string | null } | null>(null);
  const loadMoreRef = useRef<HTMLButtonElement>(null);
  /** History length when "Load more" was pressed; null when no load is pending. */
  const loadMoreFromIndex = useRef<number | null>(null);

  const isLoading = state === 'loading';
  const showsResult = (state === 'populated' || state === 'partial') && result !== null;
  const resultId = result?.id ?? null;

  // Move focus to the result (or error) heading when a new result is rendered,
  // so keyboard and screen-reader users land on it (spec: Accessibility).
  useEffect(() => {
    const prev = previous.current;
    previous.current = { state, resultId };
    if (prev === null) return;
    const finishedLoading = prev.state === 'loading' && state !== 'loading';
    const switchedResult = showsResult && prev.resultId !== resultId;
    if (!finishedLoading && !switchedResult) return;
    const target = state === 'error' ? 'error' : showsResult ? 'result' : null;
    if (target === null) return;
    rootRef.current
      ?.querySelector<HTMLElement>(`[data-comparison-focus="${target}"]`)
      ?.focus();
  }, [state, resultId, showsResult]);

  // After "Load more" finishes, move focus to the first newly loaded comparison so
  // keyboard and screen-reader users continue where the list grew; if nothing new
  // arrived, return focus to the button (it was disabled, which drops focus).
  useEffect(() => {
    const fromIndex = loadMoreFromIndex.current;
    if (fromIndex === null || isLoadingMoreHistory) return;
    loadMoreFromIndex.current = null;
    const items = rootRef.current?.querySelectorAll<HTMLElement>('[data-comparison-history-item]');
    const firstNew = items && items.length > fromIndex ? items[fromIndex] : undefined;
    (firstNew ?? loadMoreRef.current)?.focus();
  }, [isLoadingMoreHistory, history.length]);

  const handleLoadMoreHistory = () => {
    if (isLoadingMoreHistory || !onLoadMoreHistory) return;
    loadMoreFromIndex.current = history.length;
    onLoadMoreHistory();
  };

  const focusDateSelection = () => {
    const button = presetRefs.current.get(selectedPreset) ?? presetRefs.current.get('7d');
    button?.focus();
  };

  const effectiveReducedMotion = reducedMotion ?? prefersReducedMotion();

  const renderSelection = () => (
    <Section title={t('comparison.select.title')}>
      <Card padding="lg">
        <Stack gap="4">
          <Inline gap="2" wrap role="group" aria-label={t('comparison.presets.label')}>
            {PRESETS.map(preset => (
              <Button
                key={preset}
                ref={el => {
                  if (el) presetRefs.current.set(preset, el);
                  else presetRefs.current.delete(preset);
                }}
                variant={preset === 'custom' || preset === 'photos' ? 'ghost' : 'secondary'}
                disabled={isLoading}
                aria-pressed={selectedPreset === preset}
                onClick={() => onSelectPreset(preset)}
              >
                {t(`comparison.preset.${preset}`)}
              </Button>
            ))}
          </Inline>
          {selectedPreset === 'custom' && (
            <Inline gap="4" wrap className="comparison-view__dates">
              <Input
                id="comparison-date-from"
                type="date"
                name="dateFrom"
                label={t('comparison.custom.from')}
                value={customDateFrom}
                max={customDateTo || undefined}
                disabled={isLoading}
                onChange={e => onChangeCustomDateFrom(e.target.value)}
              />
              <Input
                id="comparison-date-to"
                type="date"
                name="dateTo"
                label={t('comparison.custom.to')}
                value={customDateTo}
                min={customDateFrom || undefined}
                disabled={isLoading}
                onChange={e => onChangeCustomDateTo(e.target.value)}
              />
            </Inline>
          )}
          {selectedPreset === 'photos' && photoPicker}
          {selectedPreset !== 'photos' && (state === 'populated' || state === 'empty') && (
            <Text variant="muted">{t('comparison.select.hint')}</Text>
          )}
        </Stack>
      </Card>
    </Section>
  );

  const renderLoading = () => (
    <Card padding="lg">
      <Stack gap="4">
        <Text variant="muted">{t('comparison.loading.message')}</Text>
        <Grid minItemWidth="sm" gap="4" aria-hidden="true">
          <div className="comparison-view__skeleton comparison-view__skeleton--image" />
          <div className="comparison-view__skeleton comparison-view__skeleton--image" />
        </Grid>
        <div className="comparison-view__skeleton comparison-view__skeleton--text" aria-hidden="true" />
      </Stack>
    </Card>
  );

  const renderError = () => {
    const kind: ComparisonErrorKind = errorKind ?? 'generic';
    const canAdjustDates = kind === 'insufficientImages' || kind === 'tooManyImages';
    const canAdjustPhotos =
      kind === 'photosTooFew' || kind === 'photosTooMany' || kind === 'photosInvalid';
    // Used-up monthly AI credits: retrying cannot help until the reset.
    const canRetry = kind === 'generic' || kind === 'aiUnavailable';
    return (
      <Alert
        tabIndex={-1}
        data-comparison-focus="error"
        title={t(`comparison.error.${kind}.title`)}
        titleLevel={3}
        action={
          canAdjustDates || canAdjustPhotos || canRetry ? (
            <>
              {canAdjustDates && (
                <Button variant="secondary" onClick={focusDateSelection}>
                  {t('comparison.error.action.adjustDates')}
                </Button>
              )}
              {canAdjustPhotos && (
                <Button variant="secondary" onClick={focusDateSelection}>
                  {t('comparison.error.action.adjustPhotos')}
                </Button>
              )}
              {canRetry && (
                <Button variant="secondary" onClick={onRetry}>
                  {t('comparison.error.action.retry')}
                </Button>
              )}
            </>
          ) : undefined
        }
      >
        {t(`comparison.error.${kind}.description`)}
      </Alert>
    );
  };

  const renderImageCard = (image: ComparisonImage, heading: string, key?: string) => (
    <Card padding="md" key={key}>
      <Stack gap="2">
        <Heading level={3}>{heading}</Heading>
        <Text variant="muted">{t('comparison.image.dateLabel', { date: image.date })}</Text>
        {image.isNearestMatch && (
          <Text variant="muted">{t('comparison.image.nearestDateNote')}</Text>
        )}
        <img className="comparison-view__image" src={image.src} alt={image.alt} />
      </Stack>
    </Card>
  );

  const renderResult = (current: ComparisonResult) => (
    <Card padding="lg">
      <Stack gap="4">
        <Inline gap="2" wrap>
          <Badge tone={CHANGE_TONES[current.changeCategory]}>
            {t(`comparison.change.${current.changeCategory}`)}
          </Badge>
          <Badge tone={comparabilityTone(current.comparability)}>
            {t(`comparison.comparability.${current.comparability}`)}
          </Badge>
          <Badge tone="neutral">{t(`comparison.confidence.${current.confidence}`)}</Badge>
        </Inline>
        {current.comparability === 'limited' && (
          <Text variant="muted">{t('comparison.comparability.limited.hint')}</Text>
        )}
        {state === 'partial' && <Text variant="muted">{t('comparison.partial.note')}</Text>}
        <Tabs
          aria-label={t('comparison.view.toggle')}
          activeId={viewMode}
          onChange={id => onChangeViewMode(id as ComparisonViewMode)}
          items={[
            {
              id: 'sideBySide',
              label: t('comparison.view.sideBySide'),
              panel:
                viewMode === 'sideBySide' ? (
                  <Grid minItemWidth="sm" gap="4" className="comparison-view__panel">
                    {renderImageCard(current.beforeImage, t('comparison.image.before'))}
                    {(current.intermediateImages ?? []).map((image, index) =>
                      renderImageCard(
                        image,
                        t('comparison.image.intermediate', { position: index + 1 }),
                        `intermediate-${index}`,
                      ),
                    )}
                    {renderImageCard(current.afterImage, t('comparison.image.after'))}
                  </Grid>
                ) : null,
            },
            {
              id: 'slider',
              label: t('comparison.view.slider'),
              panel:
                viewMode === 'slider' ? (
                  <div className="comparison-view__panel">
                    <BeforeAfterSlider
                      beforeImage={{ src: current.beforeImage.src, alt: current.beforeImage.alt }}
                      afterImage={{ src: current.afterImage.src, alt: current.afterImage.alt }}
                      beforeLabel={t('comparison.image.before')}
                      afterLabel={t('comparison.image.after')}
                      reducedMotion={effectiveReducedMotion}
                      ariaLabel={t('comparison.slider.label')}
                    />
                  </div>
                ) : null,
            },
          ]}
        />
        <Stack gap="2">
          <Heading level={3}>{t('comparison.insight.title')}</Heading>
          {/* AI output: interpolated with escapeValue=false and rendered as a React text node — never as HTML. */}
          <Text>{t('comparison.insight.descriptionPlaceholder', { description: current.description })}</Text>
          <Text variant="muted">
            {t('comparison.insight.sourceImages', { imageIds: current.sourceImageIds.join(', ') })}
          </Text>
        </Stack>
      </Stack>
    </Card>
  );

  const renderResultSection = () => (
    <Section
      title={t('comparison.result.title')}
      headingProps={RESULT_HEADING_PROPS}
    >
      {/* Exactly one polite region (loading → result) and one Alert (errors, role="alert"), not nested. */}
      <div aria-live="polite" aria-busy={isLoading} className="comparison-view__live">
        {isLoading && renderLoading()}
        {showsResult && result && renderResult(result)}
      </div>
      {state === 'error' && renderError()}
    </Section>
  );

  const renderHistoryItem = (item: ComparisonHistoryItem) => {
    const isSelected = item.id === selectedHistoryId;
    const summaryId = `comparison-history-${item.id}-summary`;
    return (
      <li key={item.id} className="comparison-view__history-entry">
        <Card
          padding="md"
          className={[
            'comparison-view__history-card',
            isSelected ? 'comparison-view__history-card--selected' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <Stack gap="2">
            <Inline gap="2" justify="between">
              <Heading level={3} className="comparison-view__history-heading">
                <Button
                  variant="ghost"
                  className="comparison-view__history-button"
                  aria-current={isSelected ? 'true' : 'false'}
                  aria-describedby={summaryId}
                  data-comparison-history-item=""
                  onClick={() => onSelectHistoryItem(item.id)}
                >
                  {t('comparison.history.item.dateRange', {
                    fromDate: item.fromDate,
                    toDate: item.toDate,
                  })}
                </Button>
              </Heading>
              <Badge tone="neutral">
                {t(`comparison.history.item.triggeredBy.${item.triggeredBy}`)}
              </Badge>
            </Inline>
            <Text variant="muted" id={summaryId}>
              {t('comparison.history.item.changeSummary', {
                changeCategory: t(`comparison.change.${item.changeCategory}`),
                description: item.description,
              })}
            </Text>
            <Inline gap="2" align="center" justify="between">
              <Badge tone={comparabilityTone(item.comparability)}>
                {t(`comparison.comparability.${item.comparability}`)}
              </Badge>
              {onDeleteHistoryItem && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="comparison-view__history-delete"
                  aria-label={t('comparison.history.delete.itemLabel', {
                    fromDate: item.fromDate,
                    toDate: item.toDate,
                  })}
                  disabled={isDeletingHistory}
                  onClick={() => onDeleteHistoryItem(item.id)}
                >
                  {t('comparison.history.delete.item')}
                </Button>
              )}
            </Inline>
          </Stack>
        </Card>
      </li>
    );
  };

  const renderHistorySection = () => (
    <Section
      title={t('comparison.history.title')}
      actions={
        onDeleteAllHistory && history.length > 0 ? (
          <Button variant="secondary" disabled={isDeletingHistory} onClick={onDeleteAllHistory}>
            {t('comparison.history.delete.all')}
          </Button>
        ) : undefined
      }
    >
      {historyStatus}
      {history.length === 0 ? (
        <EmptyState
          icon={<Icon name="image" size="lg" />}
          headline={t('comparison.history.empty.title')}
          description={t('comparison.history.empty.description')}
          headingLevel={3}
          actionLabel={isLoading ? undefined : t('comparison.action.compare')}
          onAction={isLoading ? undefined : onCompare}
          actionVariant="secondary"
        />
      ) : (
        <Stack gap="4">
          <Grid as="ul" minItemWidth="md" gap="4" className="comparison-view__history-list">
            {history.map(renderHistoryItem)}
          </Grid>
          {hasMoreHistory && onLoadMoreHistory && (
            <Inline gap="2">
              <Button
                ref={loadMoreRef}
                variant="secondary"
                disabled={isLoadingMoreHistory}
                aria-busy={isLoadingMoreHistory || undefined}
                onClick={handleLoadMoreHistory}
              >
                {isLoadingMoreHistory
                  ? t('comparison.history.loadingMore')
                  : t('comparison.history.loadMore')}
              </Button>
            </Inline>
          )}
        </Stack>
      )}
    </Section>
  );

  return (
    <div ref={rootRef} className="comparison-view">
      {renderSelection()}
      {state !== 'empty' && renderResultSection()}
      {renderHistorySection()}
    </div>
  );
}

export default ComparisonView;
